/**
 * System Modal - מודל הודעות מערכת
 * Reusable modal for system messages (success, error, warning, info)
 * Replaces native browser alert() with better UX and mobile responsiveness
 */

import { useRef } from 'react'
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react'
import { useDialogA11y } from '../../hooks/useDialogA11y'

export interface SystemModalProps {
  isOpen: boolean
  onClose: () => void
  type?: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  confirmText?: string
  showCancel?: boolean
  cancelText?: string
  onConfirm?: () => void
}

export default function SystemModal({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  confirmText = 'אישור',
  showCancel = false,
  cancelText = 'ביטול',
  onConfirm
}: SystemModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(isOpen, dialogRef, onClose)

  if (!isOpen) return null

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    }
    onClose()
  }

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={48} className="text-green-500" aria-hidden="true" />
      case 'error':
        return <XCircle size={48} className="text-red-500" aria-hidden="true" />
      case 'warning':
        return <AlertTriangle size={48} className="text-yellow-500" aria-hidden="true" />
      case 'info':
      default:
        return <Info size={48} className="text-blue-500" aria-hidden="true" />
    }
  }

  const getTitle = () => {
    if (title) return title

    switch (type) {
      case 'success':
        return 'הצלחה'
      case 'error':
        return 'שגיאה'
      case 'warning':
        return 'אזהרה'
      case 'info':
      default:
        return 'הודעה'
    }
  }

  const getColorClass = () => {
    switch (type) {
      case 'success':
        return 'border-green-500'
      case 'error':
        return 'border-red-500'
      case 'warning':
        return 'border-yellow-500'
      case 'info':
      default:
        return 'border-blue-500'
    }
  }

  return (
    // Backdrop click-to-dismiss is a mouse enhancement only; keyboard users
    // close via Escape (useDialogA11y) or the visible סגירה/ביטול buttons, so
    // this overlay does not need its own keyboard handler.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="system-modal-title"
        aria-describedby="system-modal-message"
        className={`bg-white rounded-lg shadow-2xl max-w-md w-full border-t-4 ${getColorClass()} animate-in fade-in zoom-in duration-200`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-center gap-4">
            {getIcon()}
            <h2 id="system-modal-title" className="text-xl font-bold text-gray-900">{getTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="סגירה"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Message */}
        <div className="px-6 pb-6">
          <p id="system-modal-message" className="text-gray-700 leading-relaxed whitespace-pre-line">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-6 pb-6 flex-col-reverse sm:flex-row sm:justify-end">
          {showCancel && (
            <button
              onClick={onClose}
              className="btn btn-outline w-full sm:w-auto"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`btn w-full sm:w-auto ${
              type === 'error'
                ? 'btn-error'
                : type === 'warning'
                ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                : type === 'success'
                ? 'btn-success'
                : 'btn-primary'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
